import { z } from 'zod';

import { makeEndpoint } from '../common';

const BaseCreditCodeSchema = z.object({
  id: z.string().uuid(),
});

const CreateCreditCodeRequestSchema = z.object({
  code: z.string(),
  externalTransactionId: z.string().nullable(),
  description: z.string().nullable(),
  cardPurchaseTransactionId: z.string().nullable(),
  redeemableByEmailAddress: z.string(),
  isRedeemableByAnyUser: z.boolean().default(false),
  redeemableOnOrganizations: z.array(z.string()).nullable(),
  redeemableOnBrands: z.array(z.string()).nullable(),
  originalValue: z.number().default(0),
  expiresAt: z.union([z.string().datetime().nullable(), z.date()]).nullable(),
});

export type CreateCreditCodeRequest = z.output<typeof CreateCreditCodeRequestSchema>;

const SearchCreditCodeRequestSchema = z.object({
  code: z.string(),
});

export type SearchCreditCodeRequest = z.output<typeof SearchCreditCodeRequestSchema>;

const CreditCodeHistorySchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  redeemedOn: z.string().datetime(),
  redeemedAmount: z.number(),
  beforeValue: z.number(),
  afterValue: z.number(),
  cartId: z.string().uuid(),
});

const CreditCodeHistoriesSchema = z.object({
  histories: z.array(CreditCodeHistorySchema).nullable(),
});

export type CreditCodeHistories = z.infer<typeof CreditCodeHistoriesSchema>;

const GetCreditCodeResponseSchema = BaseCreditCodeSchema.merge(CreateCreditCodeRequestSchema);

export type GetCreditCodeResponse = z.infer<typeof GetCreditCodeResponseSchema>;

const CreditCodeRedeemRefundRequestSchema = z.object({
  code: z.string(),
  userEmail: z.string().nullable(),
  transactionAmount: z.number().positive(),
  cartId: z.string().uuid(),
});

export type CreditCodeRedeemRequest = z.output<typeof CreditCodeRedeemRefundRequestSchema>;

export type CreditCodeRefundRequest = z.output<typeof CreditCodeRedeemRefundRequestSchema>;

const ValidateListingsRequestSchema = z.object({
  listingIds: z.array(z.string().uuid()),
});

export type ValidateListingsRequest = z.output<typeof ValidateListingsRequestSchema>;

const ValidateListingsResponseSchema = z.object({
  validListingIds: z.array(z.string().uuid()).nullable(),
  invalidListingIds: z.array(z.string().uuid()).nullable(),
});

export type ValidateListingsResponse = z.output<typeof ValidateListingsResponseSchema>;

export const CreateBatchCreditCodeRequestSchema = z
  .object({
    value: z.number().positive('Value must be greater than 0'),
    quantity: z.number().int().min(1).max(30000, 'Quantity must be between 1 and 30,000'),
    codePrefix: z.string().max(11, 'Prefix must be less than 12 characters').optional(),
    expiresAt: z.string().datetime().nullable().optional(),
    description: z.string().nullable().optional(),
    externalTransactionId: z.string().nullable().optional(),
  })
  .strict();

export type CreateBatchCreditCodeRequest = z.infer<typeof CreateBatchCreditCodeRequestSchema>;

export const CreateCreditCodeEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/credit-codes`,
  responseSchema: GetCreditCodeResponseSchema,
  requestSchema: CreateCreditCodeRequestSchema,
});

export const GetCreditCodeEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ creditCodeId }: { creditCodeId: string }) => `v1/credit-codes/${creditCodeId}`,
  responseSchema: GetCreditCodeResponseSchema,
  requestSchema: z.void(),
});

export const GeneralSearchCreditCodeEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/credit-codes/search`,
  responseSchema: GetCreditCodeResponseSchema,
  requestSchema: SearchCreditCodeRequestSchema,
});

export const RedeemCreditCodeEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ creditCodeId }: { brandId: string; creditCodeId: string }) => `v1/credit-codes/${creditCodeId}/redeem`,
  responseSchema: z.object({
    redemptionCode: z.string().uuid(), // Id of the credit code history object
  }),
  requestSchema: CreditCodeRedeemRefundRequestSchema,
});

export const RefundCreditCodeEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ creditCodeId }: { brandId: string; creditCodeId: string }) => `v1/credit-codes/${creditCodeId}/refund`,
  responseSchema: z.object({
    redemptionCode: z.string().uuid(), // Id of the credit code history object
  }),
  requestSchema: CreditCodeRedeemRefundRequestSchema,
});

export const DeleteCreditCodeEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({ creditCodeId }: { creditCodeId: string }) => `v1/credit-codes/${creditCodeId}`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});

export const GetCreditCodeHistoriesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ creditCodeId }: { creditCodeId: string }) => `v1/credit-codes/${creditCodeId}/histories`,
  responseSchema: CreditCodeHistoriesSchema,
  requestSchema: z.void(),
});

export const ValidateListingsEndpoint = makeEndpoint({
  method: 'POST',
  url: () => `v1/credit-codes/validate-listings`,
  responseSchema: ValidateListingsResponseSchema,
  requestSchema: ValidateListingsRequestSchema,
});
